import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useMediaQuery } from "react-responsive";
import { useDashboardData } from "../../hooks/useDashboardData";
import ProfileChecklist from "../../components/profileChecklist/ProfileChecklist";

import Header from "../../components/header/Header";
import Sidebar from "../../components/sidebar/Sidebar";
import "../dashboard/Dashboard.css";
import RankingCard from "../../components/dashboard/RankingCard";
import StatsGrid from "../../components/dashboard/StatsGrid";
import RatingsCard from "../../components/dashboard/RatingsCard";
import VisitorsAreaChart from "../../components/dashboard/VisitorsAreaChart";
import VisibilityPieChart from "../../components/dashboard/VisibilityPieChart";
import DateRangeFilter from "../../components/dashboard/DateRangeFilter";
import PreviewProfileButton from "../../components/dashboard/PreviewProfileButton";
import SessionRequestsCard from "../../components/dashboard/SessionRequestsCard";
import EnquiriesCard from "../../components/dashboard/EnquiriesCard";
import { useRankings } from "../../hooks/useRankings";

const SkeletonCard = () => (
  <div className="analysis-card skeleton">
    <div className="skeleton-title" />
    <div className="skeleton-chart" />
  </div>
);

const UserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [range, setRange] = useState("30d");
  const isSmallMobile = useMediaQuery({ maxWidth: 820 });

  // Dynamic title & ranking title based on role
  const isTutor = user?.role === "tutor";
  const displayName = isTutor 
    ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || "Tutor"
    : user?.academyName || "Training Center";

  const rankingTitle = isTutor 
    ? "Tutor Leaderboard Ranking" 
    : "Training Center Ranking";

  const currentUserId = user?._id;

  const { data: rankings = [], isLoading: rankingsLoading } = useRankings(currentUserId);


  const {
    stats,
    changes,
    visitorsData,
    pieData,
    ratings,
    isLoading,
    error
  } = useDashboardData(currentUserId, range);

  return (
    <div className="tutors-app">
      <Header 
        notificationCount={0} 
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)} 
      />
      <Sidebar 
        isOpen={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)} 
      />

      <main className="authenticated-main">
        <div className="content-container">
          <div className="home-header-row">
            <div>
              <h1>Welcome back, {displayName}</h1>
              <p>Track your account activity here.</p>
            </div>
            <PreviewProfileButton user={user} />
          </div>

          <ProfileChecklist
            user={user}
            onNavigate={({ tab, subTab }) =>
              navigate(`/account?tab=${tab}${subTab ? `&subTab=${subTab}` : ""}`)
            }
          />

          <DateRangeFilter value={range} onChange={setRange} />

          {(user?.role === "tutor" || user?.role === "institution") && (
            <>
              <EnquiriesCard />
              <SessionRequestsCard />
            </>
          )}

          {isSmallMobile ? (
            // Mobile Layout - Simple Stack
            <div className="home-mobile-stack">
              <StatsGrid stats={stats} changes={changes} />
              {isLoading ? <SkeletonCard /> : <VisitorsAreaChart data={visitorsData} />}
              {isLoading ? <SkeletonCard /> : <VisibilityPieChart data={pieData} />}
              <RankingCard title={rankingTitle} rankings={rankings} />
              {isLoading ? <SkeletonCard /> : <RatingsCard ratings={ratings} />}
            </div>
          ) : (
            // Desktop Layout - Proper Grid
            <>
              <div className="home-top-section">
                <RankingCard title={rankingTitle} rankings={rankings} />
                <div className="home-right-dashboard">
                  <StatsGrid stats={stats} changes={changes} />
                </div>
              </div>

              <div className="home-bottom-charts">
                {isLoading ? <SkeletonCard /> : <VisitorsAreaChart data={visitorsData} />}
                {isLoading ? <SkeletonCard /> : <VisibilityPieChart data={pieData} />}
              </div>

              {isLoading ? <SkeletonCard /> : <RatingsCard ratings={ratings} />}
            </>
          )}

          {error && <p className="error-text">Failed to load some dashboard data. Please refresh.</p>}
        </div>
      </main>

    </div>
  );
};

export default UserDashboard;